<template>
  <div class="diagnostics-view task-page">
    <v-card>
      <v-card-text>
        <v-alert v-if="error" type="error" variant="tonal" class="mb-4">
          {{ error }}
        </v-alert>
        <ul style="list-style-type: none" class="pa-0">
          <li class="d-flex align-center py-1">
            <span :class="[getIsLoadClass(pluginLoaded), 'mr-2 circle']" />
            <span class="text-body-1"> Плагин загружен</span>
          </li>
          <li class="d-flex align-center py-1">
            <span :class="[getIsLoadClass(pluginWorking), 'mr-2 circle']" />
            <span class="text-body-1">Плагин работает</span>
          </li>
          <li class="d-flex align-center py-1">
            <span :class="[getIsLoadClass(objectsEnumerated), 'mr-2 circle']" />
            <span class="text-body-1">Криптопровайдер загружен</span>
          </li>
          <li class="d-flex align-center py-1">
            <span :class="[getIsLoadClass(cryptoProviderLoaded), 'mr-2 circle']" />
            <span class="text-body-1">Перечисление объектов плагина завершено</span>
          </li>
          <li v-if="pluginVersion">Версия плагина: {{ pluginVersion }}</li>
        </ul>
      </v-card-text>
    </v-card>

    <div class="d-flex row justify-lg-space-between ma-0 pa-0 gap-2 flex-nowrap" style="gap: 20px">
      <v-card class="w-100 mt-4">
        <v-card-title>Выберите сертификат:</v-card-title>
        <v-card-text>
          <v-radio-group v-model="selectedCertificate" column>
            <template v-if="certificates.length">
              <v-row
                v-for="(cert, index) in certificates"
                :key="index"
                class="align-center ma-0 py-1 w-100"
              >
                <v-icon :color="cert.isNotYet ? 'green' : 'red'">{{
                  cert.isNotYet ? 'mdi-check' : 'mdi-close'
                }}</v-icon>
                <v-radio
                  :disabled="!cert.isNotYet"
                  class="ml-2"
                  :value="cert.thumbprint"
                  :label="cert.label"
                />
              </v-row>
            </template>
            <v-row v-else class="align-center ma-0 py-1 w-100 text-center text-grey justify-center"
              >Нет доступных сертификатов</v-row
            >
          </v-radio-group>
        </v-card-text>
      </v-card>
      <v-card class="w-100 mt-4">
        <v-card-title>Выберите МЧД (опционально)</v-card-title>
        <v-card-text>
          <input type="file" accept=".xml" class="mb-3" @change="handleAttorneyFileChange" />

          <div v-if="attorneyData" class="mt-4">
            <p v-if="attorneyData.issuer_inn">ИНН юрлица: {{ attorneyData.issuer_inn }}</p>
            <p v-if="attorneyData.issuer_name">
              Наименование юрлица: {{ attorneyData.issuer_name }}
            </p>

            <div class="mt-3">
              <p class="mb-1 font-weight-bold">Проверка МЧД</p>
              <p v-if="attorneyData.attorneyCheck?.status">Проверка пройдена успешно.</p>
              <p v-else class="text-red">
                {{ formatCheckMessage(attorneyData.attorneyCheck?.message) }}
              </p>
            </div>

            <div class="mt-3">
              <p class="mb-1 font-weight-bold">Проверка полномочий</p>
              <p v-if="!attorneyData.authorityCheck?.status" class="text-red">
                {{ formatCheckMessage(attorneyData.authorityCheck?.message) }}
              </p>
              <template v-else>
                <p>{{ attorneyData.authorityCheck?.message }}</p>
                <p
                  v-for="(authority, index) in attorneyData.authorityCheck?.authorities || []"
                  :key="index"
                  class="mb-0"
                >
                  * {{ authority.code }} - {{ authority.text }}
                </p>
              </template>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </div>

    <v-card class="mt-4">
      <v-card-title>Данные для подписи:</v-card-title>
      <v-card-text>
        <v-textarea v-model="textToSign" label="Введите данные для подписи"></v-textarea>
        <v-btn
          color="primary"
          :disabled="
            !selectedCertificate ||
            !pluginLoaded ||
            !pluginWorking ||
            !objectsEnumerated ||
            !cryptoProviderLoaded ||
            !textToSign ||
            isSigning
          "
          :loading="isSigning"
          class="mt-2"
          @click="signDocument"
          >Подписать</v-btn
        >
      </v-card-text>
    </v-card>

    <v-card v-if="currentCertificate" class="mt-4">
      <v-card-title>Информация о сертификате:</v-card-title>
      <v-card-text>
        <p>Владелец: {{ currentCertificate.name }}</p>
        <p>Издатель: {{ currentCertificate.issuerName }}</p>
        <p>Срок действия: {{ currentCertificate.validTo }}</p>
        <p>
          Статус:
          <span :class="currentCertificate.isNotYet ? '' : 'text-red'">{{
            currentCertificate.status
          }}</span>
        </p>
      </v-card-text>
    </v-card>
    <v-card v-if="sign" class="mt-4">
      <v-card-title>Подпись сформирована успешно:</v-card-title>
      <v-card-text>
        <v-textarea v-model="sign" />
        <v-row class="mx-0 my-2 pa-0 justify-end">
          <v-btn color="primary" prepend-icon="mdi-content-copy" class="mt-2" @click="copySign"
            >Копировать</v-btn
          >
        </v-row>
      </v-card-text>
    </v-card>
  </div>
</template>
<script setup>
import store from '@/store'
import { computed, onMounted, ref } from 'vue'
import { createDetachedSignature, getUserCertificates, createHash } from 'crypto-pro'
import moment from 'moment'
import { getFormatDateTime } from '@/helpers'
import { useToast } from '@/composables/useToast'
import { api } from '@/api/Api'

const { toast } = useToast()

const certificates = ref([])
const selectedCertificate = ref('')
const textToSign = ref('Hello World')
const error = ref('')
const pluginLoaded = ref(false)
const pluginVersion = ref('')
const pluginWorking = ref(false)
const cryptoProviderLoaded = ref(false)
const objectsEnumerated = ref(false)
const officialCertificate = ref([])
const sign = ref()
const attorneyFile = ref(null)
const isSigning = ref(false)
const attorneyData = ref(null)

const currentCertificate = computed(
  () => certificates.value?.find((el) => el.thumbprint === selectedCertificate.value) || null,
)

const getIsLoadClass = (value) => {
  return value ? 'green' : 'red'
}

const resetAttorneyCheck = () => {
  attorneyFile.value = null
  attorneyData.value = null
}

const performAttorneyCheck = async (subjectName = '') => {
  if (!attorneyFile.value) {
    return null
  }

  const { inn, inn_ul } = getCertificateInnValues(subjectName)
  const formData = new FormData()
  formData.append('attorneyFile', attorneyFile.value)
  formData.append('inn', inn)
  formData.append('inn_ul', inn_ul)

  return api.projects().checkAttorney(formData)
}

const getSigningCertificateSubject = async (thumbprint) => {
  const certs = await getUserCertificates()
  const cert = certs.find((c) => c.thumbprint === thumbprint)

  if (!cert) {
    return certificates.value.find((c) => c.thumbprint === thumbprint)?.subjectName || ''
  }

  if (cert._cadesCertificate) {
    try {
      const subjectName = await cert._cadesCertificate.SubjectName
      if (subjectName) {
        return subjectName
      }
    } catch (err) {
      console.log(err)
    }
  }

  return cert.subjectName || ''
}

const resolveCertificateSubject = async (thumbprint) => {
  try {
    return await getSigningCertificateSubject(thumbprint)
  } catch (err) {
    console.log(err)
    return certificates.value.find((c) => c.thumbprint === thumbprint)?.subjectName || ''
  }
}

const handleAttorneyFileChange = (event) => {
  attorneyFile.value = event.target.files[0] || null
  attorneyData.value = null
}

const formatCheckMessage = (message) => {
  if (Array.isArray(message)) {
    return message.join(' ')
  }

  return message || ''
}

const extractFromSubject = (subject, keys) => {
  if (!subject) {
    return ''
  }

  for (const key of keys) {
    const regex = new RegExp(
      `(?:^|[,\\s])${key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}([^,]+)`,
      'iu',
    )
    const match = subject.match(regex)
    if (match?.[1]) {
      return match[1].replace(/["']/g, '').trim()
    }
  }

  return ''
}

const getCertificateInnValues = (subjectName) => {
  if (!subjectName) {
    return { inn: '', inn_ul: '' }
  }

  const parts = subjectName.split(',').map((part) => part.trim())
  let inn = ''
  let inn_ul = ''

  for (const part of parts) {
    const value = part.split('=').slice(1).join('=').replace(/["']/g, '').trim()

    if (/^(?:INNLE|ИНН\s*ЮЛ|1\.2\.643\.100\.4|OID\.1\.2\.643\.100\.4)=/iu.test(part)) {
      inn_ul = value
      continue
    }

    if (
      /^(?:INN|ИНН|1\.2\.643\.3\.131\.1\.1|1\.2\.643\.100\.3|OID\.1\.2\.643\.3\.131\.1\.1)=/iu.test(
        part,
      )
    ) {
      inn = value
    }
  }

  if (!inn) {
    inn = extractFromSubject(subjectName, [
      'INN=',
      'ИНН=',
      '1.2.643.3.131.1.1=',
      '1.2.643.100.3=',
      'OID.1.2.643.3.131.1.1=',
    ])
  }

  if (!inn_ul) {
    inn_ul = extractFromSubject(subjectName, [
      'INNLE=',
      'ИНН ЮЛ=',
      '1.2.643.100.4=',
      'OID.1.2.643.100.4=',
    ])
  }

  return { inn, inn_ul }
}

const copySign = () => {
  navigator.clipboard
    .writeText(sign.value)
    .then(() => {
      toast.success('Подпись скопирована в буфер обмена')
    })
    .catch((err) => {
      console.error('Ошибка при копировании в буфер обмена: ', err)
    })
}
const checkCadesPlugin = async () => {
  await window.cadesplugin.then(() => {
    pluginLoaded.value = true
    pluginWorking.value = true
  })
  const cadesStore = await window.cadesplugin.CreateObjectAsync('CAdESCOM.Store')

  await cadesStore.Open()
  const certificates = await cadesStore.Certificates
  const count = await certificates.Count

  if (count > 0) {
    cryptoProviderLoaded.value = true
    objectsEnumerated.value = true
  } else {
    throw new Error('Сертификаты не найдены.')
  }

  await cadesStore.Close()
  try {
    if (!window.cadesplugin) {
      throw new Error('Плагин CryptoPro не загружен. Убедитесь, что он установлен.')
    }
  } catch (e) {
    error.value = e.message
    pluginWorking.value = false
  }
}

const fetchCertificates = async () => {
  try {
    sign.value = ''
    resetAttorneyCheck()
    const certs = await getUserCertificates()
    officialCertificate.value = certs
    certificates.value = certs.map((cert) => ({
      ...cert,
      label: `${cert.name || ''}; Выдан: ${moment(cert.validFrom).format(
        'DD.MM.YYYY HH:mm',
      )}; Действует до: ${moment(cert.validTo).format('DD.MM.YYYY HH:mm')}`,
      thumbprint: cert.thumbprint,
      details: cert,
      isNotYet: moment().isBefore(moment(cert.validTo, 'YYYY-MM-DD HH:mm')),
      subjectName: cert.subjectName,
      issuerName: cert.issuerName,
      validFrom: getFormatDateTime(cert.validFrom),
      validTo: getFormatDateTime(cert.validTo),
      status: moment().isBefore(moment(cert.validTo, 'YYYY-MM-DD HH:mm'))
        ? 'Действителен'
        : 'Срок действия истек',
    }))
    error.value = ''
  } catch (e) {
    error.value = `Ошибка получения сертификатов: ${e.message}`
  }
}

async function hasPrivateKey(certificate) {
  try {
    const hasKey = await certificate._cadesCertificate?.HasPrivateKey()
    return hasKey
  } catch (error) {
    console.error('Ошибка проверки привязанного ключа:', error?.message || error)
    return false
  }
}

async function isCertificateTrusted(certificate) {
  try {
    const validationResult = await certificate?._cadesCertificate?.IsValid()
    const isValid = await validationResult.Result
    return isValid
  } catch (error) {
    console.error('Ошибка проверки доверия сертификату:', error?.message || error)
    return false
  }
}

const signDocument = async () => {
  sign.value = ''
  attorneyData.value = null
  isSigning.value = true

  try {
    if (!selectedCertificate.value) {
      throw new Error('Выберите сертификат для подписи.')
    }

    const certs = await getUserCertificates()
    if (!certs.length) {
      throw new Error('Нет доступных сертификатов для подписи.')
    }

    officialCertificate.value = certs

    const currentCertificate = certs.find((cert) => cert.thumbprint === selectedCertificate.value)
    if (!currentCertificate?.thumbprint) {
      throw new Error('Нет доступных сертификатов для подписи.')
    }

    const signOptions = {
      detached: true,
      includeWholeChain: true,
    }
    const isValidPrivateKey = await hasPrivateKey(currentCertificate)
    const isCertTrusted = await isCertificateTrusted(currentCertificate)

    if (!isValidPrivateKey) {
      throw new Error('Ошибка проверки цепочки доверия. Попробуйте использовать другой сертификат')
    }

    if (!isCertTrusted) {
      throw new Error(
        'Ошибка приватного ключа сертификата. Попробуйте использовать другой сертификат',
      )
    }

    const hash = await createHash(textToSign.value)
    const signature = await createDetachedSignature(
      currentCertificate.thumbprint,
      hash,
      signOptions,
    )
    error.value = ''
    sign.value = signature

    if (attorneyFile.value) {
      const subjectName = await resolveCertificateSubject(currentCertificate.thumbprint)

      try {
        attorneyData.value = await performAttorneyCheck(subjectName)
      } catch (err) {
        toast.error(err?.response?.data?.message || err?.message || 'Ошибка при проверке МЧД')
      }
    }
  } catch (err) {
    sendFeedbackForm(window?.lastErrorObject)
    error.value = `Ошибка подписи: ${err?.message || err}`
  } finally {
    isSigning.value = false
  }
}

const sendFeedbackForm = async (err) => {
  try {
    const formData = new FormData()
    formData.append('theme', `${process.env.VUE_APP_TECHNICAL_PROBLEM_CODE}`)
    formData.append('project', process.env.VUE_APP_PROJECT_ID)
    formData.append('subject', 'Диагностика создания электронной подписи')
    const message = `Владелец: ${currentCertificate.value?.name}\nИздатель: ${
      currentCertificate.value?.issuerName
    }\nСрок действия: ${currentCertificate.value.validTo}\nСтатус: ${
      currentCertificate.value.status
    }\nОшибка: ${err?.message || err || ''}`
    formData.append('message', message)
    api
      .feedback()
      .sendFeedbackForm(formData)
      .then(() => {})
      .catch((err) => {
        if (err?.message || err) {
          toast.error(err?.message || err)
        }
      })
  } catch (err) {
    console.log(err)
  }
}

const setGlobalError = () => {
  const originalConsoleError = console.error
  console.error = function (...args) {
    originalConsoleError.apply(this, args)

    if (args[0] && typeof args[0] === 'object') {
      window.lastErrorObject = args[0]
    }
  }
}

const setPageTitle = () => {
  fetchCertificates()
  checkCadesPlugin()
  store.commit('app/setPageTitle', 'Диагностика ЭП и МЧД')
  setGlobalError()
}

onMounted(setPageTitle)
</script>
<style lang="scss" scoped>
.diagnostics-view {
  flex: 1;
}

.circle {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  display: block;
}
</style>
